import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormHelperText,
} from '@mui/material';
import { FormNumberField, FormSelect, FormTextField } from 'components/forms';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSendRejectedDeviationEmail } from 'shared/hooks';
import { useSendDeviationApprovedEmail } from 'shared/hooks/sendDeviationApprovedEmail';
import { useSendMarketPriceApprovedEmail } from 'shared/hooks/sendMarketPriceApprovedEmail';
import { useSendRejectedMarketPriceEmail } from 'shared/hooks/sendRejectedMarketPriceEmail';
import { useConfirmPriceEdit } from 'shared/hooks/useConfirmPriceEdit';
import { DynamicEmailData } from 'shared/services/email-address-service';
import { determineVariance, formatFourDecimalMoney } from 'shared/utility';
import { ApprovalDashboardData, ApprovedEmailData, RejectionEmailData } from 'types/backend';

interface EditStatusFormProps {
    open: boolean;
    row?: ApprovalDashboardData;
    onClose: () => void;
}

const options = [
    { value: 'Approved', displayText: 'Approved' },
    { value: 'Needs Approval', displayText: 'Needs Approval' },
    { value: 'Rejected', displayText: 'Rejected' },
];

export function EditStatusForm({ open, row, onClose }: EditStatusFormProps) {
    const { onStatusSubmit } = useConfirmPriceEdit();
    const { enqueueSnackbar } = useSnackbar();
    const [menuOptions, setMenuOptions] = useState([...options]);
    const url = window.location.origin;
    const dynamicEmailData: Omit<DynamicEmailData, 'currentUserName' | 'currentUserEmailAddress'> = {
        territorySalesLeaderName: row?.territorySalesLeader || '',
        territorySalesLeaderEmail: row?.territorySalesLeaderEmail || '',
        territorySalesRepName: row?.territorySalesRep || '',
        territorySalesRepEmail: row?.territorySalesRepEmail || '',
    };

    const sendDeviationRejectionEmail = useSendRejectedDeviationEmail(`${url}/approvals`, dynamicEmailData);
    const sendMarketPriceRejectionEmail = useSendRejectedMarketPriceEmail(`${url}/approvals`, dynamicEmailData);
    const sendMarketPriceApprovalEmail = useSendMarketPriceApprovedEmail(`${url}/approvals`, dynamicEmailData);
    const sendDeviationApprovalEmail = useSendDeviationApprovedEmail(`${url}/approvals`, dynamicEmailData);

    const {
        handleSubmit,
        control,
        setValue,
        watch,
        formState: { errors },
    } = useForm<ApprovalDashboardData>();

    useEffect(() => {
        if (row) {
            setMenuOptions(options.filter((o) => o.value !== row?.status));
        }
        if (setValue) {
            setValue('status', '');
            setValue('suggestedPrice', undefined);
            setValue('comments', '');
        }
    }, [row, setMenuOptions, setValue]);

    const submitStatusEdit = handleSubmit(async (form: ApprovalDashboardData) => {
        if (form.status.toLowerCase() === 'rejected' && row) {
            const emailData: RejectionEmailData = {
                createdBy: row.requestor,
                customerName: row.customerName,
                customerAccount: row.customerNumber,
                endUserName: row.endUser ?? 'N/A',
                endUserAccount: row.endUserNumber ?? 'N/A',
                customerTier: row.tier,
                territorySalesLeader: row.territorySalesLeader,
                territorySalesRep: 'Not Yet Available',
                pricingGroup: row.priceGroup ?? 'N/A',
                isOneTimeOnly: row.isOneTimeOnly,
                productId: row.productId,
                newPrice: formatFourDecimalMoney(row.requestedPrice),
                currentPrice: formatFourDecimalMoney(row.currentPrice),
                suggestedPrice: form.suggestedPrice ? formatFourDecimalMoney(form.suggestedPrice) : 'N/A',
                tierOneTargetPrice: formatFourDecimalMoney(row.tierOneTargetPrice),
                customerTierTargetPrice: formatFourDecimalMoney(row.targetPrice),
                variance: determineVariance(row.requestedPrice, row.targetPrice),
                approverName: row.approver,
                comments: form.comments ?? 'N/A',
                approverEmails: [row.email],
            };
            try {
                if (row.priceType.toLowerCase() === 'deviation') {
                    await sendDeviationRejectionEmail.mutateAsync(emailData);
                } else if (row.priceType.toLowerCase() === 'market price') {
                    await sendMarketPriceRejectionEmail.mutateAsync(emailData);
                }
            } catch (e) {
                enqueueSnackbar('Rejection email failed to send', { variant: 'error' });
            }
        } else if (form.status.toLowerCase() === 'approved' && row) {
            const emailData: ApprovedEmailData = {
                customerName: row.customerName,
                createdBy: row.requestor,
                customerAccount: row.customerNumber,
                endUserName: row.endUser ?? 'N/A',
                endUserAccount: row.endUserNumber ?? 'N/A',
                productId: row.productId,
                pricingGroup: row.priceGroup ?? 'N/A',
                isOneTimeOnly: row.isOneTimeOnly,
                productGroup: row.productGroup,
                territorySalesLeader: row.territorySalesLeader,
                territorySalesRep: 'Not yet available',
                newPrice: formatFourDecimalMoney(row.requestedPrice),
                approverName: row.approver,
                comments: form.comments ?? 'N/A',
                requestorEmail: [row.email],
            };
            try {
                if (row.priceType.toLowerCase() === 'deviation') {
                    await sendDeviationApprovalEmail.mutateAsync(emailData);
                } else if (row.priceType.toLowerCase() === 'market price') {
                    await sendMarketPriceApprovalEmail.mutateAsync(emailData);
                }
            } catch (e) {
                enqueueSnackbar('Approval email failed to send', { variant: 'error' });
            }
        }
        onStatusSubmit({
            ...row,
            ...form,
            comments: row?.comments ?? '',
            approvalRejectionNote: form.comments,
        });
        return onClose();
    });

    return row ? (
        <Dialog open={open} aria-labelledby='responsive-dialog-title'>
            <DialogTitle id='responsive-dialog-title'>{`Details for product: ${row?.productId}`}</DialogTitle>
            <DialogContent>
                <DialogContentText>Price Status</DialogContentText>
                <Box sx={{ paddingTop: '20px' }}>
                    <FormSelect
                        name={'status'}
                        label='Status'
                        control={control}
                        rules={{ required: 'Status is required' }}
                        options={menuOptions}
                        fieldError={errors?.status}
                    />
                    <FormHelperText error sx={{ paddingLeft: '15px' }}>
                        {' '}
                        {errors?.status?.message}{' '}
                    </FormHelperText>
                </Box>
                <Box sx={{ paddingTop: '20px' }}>
                    <FormTextField name={'comments'} label='Comments' control={control} fieldError={errors?.comments} />
                </Box>
                {watch('status').toLowerCase() === 'rejected' && (
                    <Box sx={{ paddingTop: '20px' }}>
                        <FormNumberField
                            name={'suggestedPrice'}
                            control={control}
                            label='Suggested Price'
                            type='money'
                            rules={{
                                min: { value: 0, message: 'Suggested Price cannot be negative' },
                                max: { value: 5, message: 'Suggested Price cannot exceed $5.00' },
                            }}
                            fieldError={errors?.suggestedPrice}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={onClose}>
                    Exit
                </Button>
                <Button onClick={submitStatusEdit} autoFocus>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    ) : (
        <></>
    );
}
